import { useBoxRenewalError } from './useBoxRenewalError';
import { useOrganizerParticipantPage } from './useOrganizerParticipantPage';
import { questionOpener } from '@src/utils/questionAnalytics';
import { hasPremiumFeature } from '@lub0v/santa-utils';
import { useEditBoxDraw } from './useEditBoxDraw';
import { WrenchIcon } from '@src/components/icons/WrenchIcon';
import { ParticipantSearch } from '@src/components/ParticipantTable/ParticipantSearch';
import { OrganizerSection } from './OrganizerSection';
import { OrganizersPanel } from '@src/pages/BoxPage/OrganizersPanel';
import { usePersonalLinkCopied } from '@src/pages/BoxPage/usePersonalLinkCopied';
import { BoxActivity } from '@src/pages/BoxPage/BoxActivity';
import { ParticipantStatusColumns } from './ParticipantStatusColumns';
import { participantGifts } from './ParticipantGiftIndicators';
import { useParticipantPreview } from '@src/pages/BoxPage/ParticipantPreview';
import { useConfirmBoxDraw } from './useConfirmBoxDraw';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMobileBoxLayout } from './BoxLayouts';
import { LinkIcon } from '@src/components/icons/LinkIcon';
import { CircleDrawHelp, AfterDrawHelp } from './DrawHelp';
import { Link as InlineLink } from '@src/components/Link/Link';
import { ThemeArtwork } from '@src/theme/Artwork';
import { RefreshIcon } from '@src/components/icons/RefreshIcon';
import { QuestionIcon } from '@src/components/icons/QuestionIcon';
import { GiftIcon } from '@src/components/icons/GiftIcon';
import { useTranslation } from 'react-i18next';
import { normalizeQuickBoxName, type BoxInfo, type CardInfo } from '@lub0v/santa-utils';
import {
  Badge,
  Button,
  Checkbox,
  Select,
  CloseIcon,
  Hint,
  Cluster,
  Input,
  NamesInput,
  ParticipantTable,
  ParticipantTableFill,
  ParticipantTableRowList,
  ParticipantRow,
  useParticipantTableViewport,
  Stack,
  Switch,
  Typography,
  closeDialog,
  showDialog,
  showToast,
  type MenuItem,
} from '@src/components';
import { EmailInviteForm, emailInviteError, emailInviteSendError } from '@src/pages/BoxPage/EmailInviteForm';
import { InviteMessageForm } from '@src/pages/BoxPage/InviteMessageForm';
import { SecretPair } from '@src/pages/BoxPage/SecretPair';
import {
  buildPersonalInviteMessage,
  buildSharedInviteMessage,
  composeInviteClipboard,
} from '@src/pages/BoxPage/inviteMessage';
import { useLinkInvitedIds } from '@src/pages/BoxPage/linkInviteStorage';
import { participantStatus, participantStatusTone } from '@src/pages/BoxPage/participantStatus';
import { sortOrganizerCards } from '@src/pages/BoxPage/sortCards';
import { sameOriginUrl } from '@src/routing/urls';
import { useGameExclusionsDialog } from './GameExclusionsDialog';
import { useBoxExclusions } from './useBoxExclusions';
import { InvitationGuide } from './InvitationGuide';
import { boxMessengerPath } from '@src/routing/routes';
import { useParticipantSelection } from './useParticipantSelection';
import './OrganizerHome.css';
import { santaClient } from '@src/state/client';
import { editBoxInput } from '@src/pages/BoxPage/boxInput';

export interface OrganizerHomeProps {
  info: BoxInfo;
  cards: CardInfo[];
  onChanged: () => Promise<void> | void;
}


export function OrganizerHome({ info, cards: initialCards, onChanged }: OrganizerHomeProps) {
  const paged = info.participantsNumber >= 50;
  const [pageRetry, setPageRetry] = useState(0);
  const pageRevision = useMemo(() => ({ info, pageRetry }), [info, pageRetry]);
  const [pageRequest, setPageRequest] = useState({ page: 1, pageSize: 12, search: '', filters: '', sort: '' });
  const remote = useOrganizerParticipantPage(info.box.key, paged, pageRequest, pageRevision);
  const cards = paged ? remote.cachedCards : initialCards;
  const loadActionCards = async () => {
    if (!paged) return cards;
    const response = await santaClient.getCards({ key: info.box.key });
    if (!response.ok || !response.data) throw new Error('Participants unavailable');
    return sortOrganizerCards(response.data);
  };
  const navigate = useNavigate();
  const location = useLocation();
  const consumedPreview = useRef<string | null>(null);
  const mobile = useMobileBoxLayout();
  const readyToDraw = (paged ? remote.data?.summary.named ?? 0 : cards.filter((card) => card.username).length) >= 3;
  const openExclusions = useGameExclusionsDialog(info, loadActionCards, onChanged);
  const editParticipantExclusions = useBoxExclusions(info, loadActionCards, onChanged);
  const { t, i18n } = useTranslation();
  const handleRenewalError = useBoxRenewalError(info);
  const openParticipantPreview = useParticipantPreview();
  const [savingDrawMode, setSavingDrawMode] = useState(false);
  async function saveDrawMode(useCircleDraw: boolean) {
    if (savingDrawMode || info.box.isArchived || info.isActiveDraw) return;
    setSavingDrawMode(true);
    try {
      const result = await santaClient.updateBox({ ...editBoxInput(info), useCircleDraw });
      if (!result.ok) {
        showToast(t(result.error?.message || 'newBox.saveFailed'));
        return;
      }
      await onChanged();
      showToast(t('game.settings.saved'), 'success');
    } catch {
      showToast(t('newBox.saveFailed'));
    } finally {
      setSavingDrawMode(false);
    }
  }

  const [adding, setAdding] = useState<string[]>([]);
  const [addingBusy, setAddingBusy] = useState(false);
  const [addingFailed, setAddingFailed] = useState(false);
  const [namesProblem, setNamesProblem] = useState<string | null>(null);
  const [emailDialog, setEmailDialog] = useState<{
    card: CardInfo;
    email: string;
    consent: boolean;
    emailError: string | null;
    error: string | null;
  } | null>(null);
  const [copyDialog, setCopyDialog] = useState<{
    card: CardInfo;
    link: string;
  } | null>(null);
  const [sharedOpen, setSharedOpen] = useState(false);
  const copyMessageRef = useRef('');
  const sharedMessageRef = useRef('');
  const [rename, setRename] = useState<{
    card: CardInfo;
    name: string;
    error: string | null;
  } | null>(null);
  const [pair, setPair] = useState<{
    giver: CardInfo;
    receiver: CardInfo;
    kind: 'giftee' | 'santa';
  } | null>(null);
  const archived = Boolean(info.box.isArchived);
  const isActiveDraw = Boolean(info.isActiveDraw);
  const sharedLink = info.inviteLink ? sameOriginUrl(info.inviteLink) : '';
  const cardIds = useMemo(() => cards.map((card) => card.id), [cards]);
  const { copiedSet, markLinkInvited } = useLinkInvitedIds(info.box.key, cardIds);
  const recordLinkCopied = usePersonalLinkCopied(info.box.key, markLinkInvited, onChanged);
  const [pageSizeChoice, setPageSizeChoice] = useState('auto');
  const [sorts, setSorts] = useState<{ column: 'participant' | 'outgoing' | 'incoming' | 'giftee'; descending: boolean }[]>([]);
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const orderedCards = useMemo(() => sortOrganizerCards(cards).sort((a, b) => {
    const rank: Record<string, number> = { unknown: 0, pending: 1, sent: 2, received: 3 };
    for (const sort of sorts) {
      let comparison = 0;
      if (sort.column === 'participant') comparison = a.username.localeCompare(b.username, i18n.language, { numeric: true, sensitivity: 'base' });
      else if (sort.column === 'giftee') comparison = (a.isWardKnown ? 2 : a.wardId ? 1 : 0) - (b.isWardKnown ? 2 : b.wardId ? 1 : 0);
      else comparison = rank[participantGifts(a, cards)[sort.column]] - rank[participantGifts(b, cards)[sort.column]];
      if (comparison) return comparison * (sort.descending ? -1 : 1);
    }
    return 0;
  }), [cards, sorts, i18n.language]);
  const sorted = useMemo(() => orderedCards.filter((card) => statusFilters.every((statusFilter) => {
    switch (statusFilter) {
      case 'unassigned': return !card.wardId;
      case 'unseen': return !card.isWardKnown;
      case 'unopened': return !card.isVerified;
      case 'incomplete': return !card.username?.trim()
        || (info.box.useWish && !(card.wishPreview?.length || card.wish?.trim()))
        || (info.box.usePost && !(card.postalAddressPreview?.length || card.postalAddress?.trim()))
        || (info.box.isPhoneRequired && !(card.phonePreview?.length || card.phone?.trim()));
      case 'unsent': {
        const outgoing = participantGifts(card, cards).outgoing;
        return outgoing !== 'sent' && outgoing !== 'received';
      }
      case 'unreceived': return !card.isGiftReceived;
      default: return true;
    }
  })), [orderedCards, statusFilters, info.box, cards]);
  const confirmDraw = useConfirmBoxDraw(info, loadActionCards, onChanged);
  const editDraw = useEditBoxDraw(info, loadActionCards, onChanged);
  const { tableRef, ...participants } = useParticipantTableViewport({
    items: paged ? remote.data?.items ?? [] : sorted,
    remoteTotal: paged ? remote.data?.total ?? info.participantsNumber : undefined,
    alwaysUseMaxPageSize: paged,
    willPaginate: paged ? true : undefined,
    pageSize: pageSizeChoice === 'auto' ? undefined : Number(pageSizeChoice),
    alwaysSearch: true,
    getSearchText: (card) => card.username || '',
  });
  const { query } = participants;
  const requestSort = sorts.map(sort => `${sort.column}:${sort.descending ? 'desc' : 'asc'}`).join(',');
  const requestFilters = statusFilters.join(',');
  useEffect(() => {
    if (!paged) return;
    setPageRequest({ page: participants.page, pageSize: participants.pageSize ?? participants.maxRows,
      search: query, filters: requestFilters, sort: requestSort });
  }, [paged, participants.page, participants.pageSize, participants.maxRows, query, requestFilters, requestSort]);
  useEffect(() => {
    const cardId = location.state?.previewCardId;
    if (!info.isAdmin || typeof cardId !== 'number' || consumedPreview.current === location.key) return;
    if (paged) {
      consumedPreview.current = location.key;
      void fetch(`/api/box/${encodeURIComponent(info.box.key)}/participants?cardId=${cardId}&pageSize=1`)
        .then(async response => {
          if (!response.ok) throw new Error('Participant unavailable');
          const data = await response.json();
          if (!data.items[0]) throw new Error('Participant unavailable');
          openParticipantPreview({ card: data.items[0], people: [...data.items, ...data.related], box: info.box,
            index: 0, copiedIds: copiedSet, onChanged, isActiveDraw: Boolean(info.isActiveDraw) });
          const { previewCardId: _previewCardId, ...state } = location.state;
          navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, { replace: true, state });
        }).catch(() => showToast(t('errors.UNKNOWN')));
      return;
    }
    const index = orderedCards.findIndex(card => card.id === cardId);
    if (index < 0) return;
    consumedPreview.current = location.key;
    if (participants.pageSize) participants.setPage(Math.floor(index / participants.pageSize) + 1);
    openParticipantPreview({ card: orderedCards[index], people: cards, box: info.box,
      index, copiedIds: copiedSet, onChanged, isActiveDraw: Boolean(info.isActiveDraw) });
    const { previewCardId: _previewCardId, ...state } = location.state;
    navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, { replace: true, state });
  }, [location, info, orderedCards, cards, copiedSet, onChanged, openParticipantPreview, participants.pageSize, participants.setPage, navigate, paged, t]);

  const selection = useParticipantSelection(info, cards, onChanged);
  const canManage = info.isAdmin && !archived;
  const unassignedCount = isActiveDraw ? (paged ? remote.data?.summary.unassigned ?? 0 : cards.filter((card) => !card.wardId).length) : 0;

  async function addNames(names: string[]) {
    const unique = names.map((name) => name.trim()).filter(Boolean);
    if (!unique.length || archived || addingBusy) {
      return;
    }
    let saved = false;
    setAdding(unique);
    setAddingBusy(true);
    setAddingFailed(false);
    try {
      const res = await santaClient.invite({
        key: info.box.key,
        isInviteAfterDraw: Boolean(info.isActiveDraw),
        users: [],
        participantNames: unique,
      });
      if (!res.ok) {
        if (handleRenewalError(res.error?.message)) return;
        setAddingFailed(true);
        showToast(t(res.error?.message || 'game.people.addFailed'));
        return;
      }
      saved = true;
      setAdding([]);
      participants.setSearch('');
      await onChanged();
    } catch {
      setAddingFailed(!saved);
      showToast(t('game.people.addFailed'));
    } finally {
      setAddingBusy(false);
    }
  }

  useEffect(() => {
    if (!emailDialog) {
      return;
    }
    showDialog({
      title: t('box.organizer.email.title', { name: emailDialog.card.username }),
      children: (
        <EmailInviteForm
          intro={t('box.organizer.email.body')}
          email={emailDialog.email}
          onEmailChange={(email) =>
            setEmailDialog((current) =>
              current ? { ...current, email, emailError: null, error: null } : current,
            )
          }
          onEmailBlur={() =>
            setEmailDialog((current) =>
              current
                ? { ...current, emailError: emailInviteError(current.email, t) ?? null }
                : current,
            )
          }
          emailLabel={t('box.organizer.email.label')}
          hint={t('box.organizer.email.hint')}
          consentGiven={emailDialog.consent}
          onConsentChange={(consent) =>
            setEmailDialog((current) => (current ? { ...current, consent } : current))
          }
          emailError={emailDialog.emailError}
          error={emailDialog.error}
          testId="game-email-form"
        />
      ),
      cancel: t('box.cancel'),
      submit: t('box.organizer.email.submit'),
      submitDisabled: !emailDialog.consent || Boolean(emailInviteError(emailDialog.email, t)),
      onSubmit: async () => {
        const email = emailDialog.email.trim();
        const invalid = emailInviteError(email, t);
        if (invalid) {
          setEmailDialog((current) => (current ? { ...current, emailError: invalid } : current));
          throw new Error('invalid email');
        }
        const res = await santaClient.sendCardLink({
          key: info.box.key,
          id: emailDialog.card.id,
          email,
        });
        if (!res.ok) {
          setEmailDialog((current) =>
            current
              ? { ...current, error: emailInviteSendError(res.error, t) }
              : current,
          );
          throw new Error('email failed');
        }
        showToast(t('box.organizer.emailSent', { name: emailDialog.card.username }), 'success');
        setEmailDialog(null);
      },
      onCancel: () => setEmailDialog(null),
      testId: 'game-email-dialog',
    });
  }, [emailDialog, info.box.key, t]);

  useEffect(() => {
    if (!copyDialog) {
      return;
    }
    showDialog({
      title: t('box.organizer.copyLink.title'),
      mobileFullScreen: true,
      children: (
        <InviteMessageForm
          message={copyMessageRef.current}
          onMessageChange={(message) => {
            copyMessageRef.current = message;
          }}
          messageLabel={t('box.invite.messageLabel')}
          messageHint={t('box.invite.linkPlaceholderHint')}
          link={copyDialog.link}
          onLinkCopied={() => { void recordLinkCopied(copyDialog.card.id); }}
          linkLabel={t('box.organizer.copyLink.linkLabel')}
          warning={t('box.organizer.copyLink.warning')}
          testId="game-copy-form"
        />
      ),
      cancel: t('box.cancel'),
      submit: t('box.organizer.copyLink.submit'),
      submitDisabled: !copyDialog.link,
      onSubmit: async () => {
        const text = composeInviteClipboard(copyMessageRef.current, copyDialog.link, [
          t('box.invite.personal.openLink'),
          t('box.invite.shared.openLink'),
        ]);
        try {
          await navigator.clipboard.writeText(text);
        } catch {
          showToast(t('box.organizer.copyFailed'));
          throw new Error('copy failed');
        }
        void recordLinkCopied(copyDialog.card.id);
        showToast(t('box.organizer.messageCopied'), 'success');
        setCopyDialog(null);
      },
      onCancel: () => setCopyDialog(null),
      testId: 'game-copy-dialog',
    });
  }, [copyDialog, t, recordLinkCopied]);

  useEffect(() => {
    if (!sharedOpen) {
      return;
    }
    showDialog({
      title: t('box.organizer.shared.title'),
      mobileFullScreen: true,
      children: (
        <InviteMessageForm
          message={sharedMessageRef.current}
          onMessageChange={(message) => {
            sharedMessageRef.current = message;
          }}
          messageLabel={t('box.invite.messageLabel')}
          messageHint={t('box.invite.linkPlaceholderHint')}
          link={sharedLink}
          linkLabel={t('box.organizer.shared.linkLabel')}
          warning={t('box.organizer.shared.warning')}
          testId="game-shared-form"
        />
      ),
      cancel: t('box.cancel'),
      submit: t('box.organizer.shared.submit'),
      onSubmit: async () => {
        const text = composeInviteClipboard(sharedMessageRef.current, sharedLink, [
          t('box.invite.shared.openLink'),
          t('box.invite.personal.openLink'),
        ]);
        try {
          await navigator.clipboard.writeText(text);
        } catch {
          showToast(t('box.organizer.copyFailed'));
          throw new Error('copy failed');
        }
        showToast(t('box.organizer.messageCopied'), 'success');
        setSharedOpen(false);
      },
      onCancel: () => setSharedOpen(false),
      testId: 'game-shared-dialog',
    });
  }, [sharedOpen, sharedLink, t]);

  useEffect(() => {
    if (!rename) {
      return;
    }
    showDialog({
      title: t('box.organizer.renameParticipant.title'),
      children: (
        <Input
          value={rename.name}
          onValueChange={(name) =>
            setRename((current) => (current ? { ...current, name, error: null } : current))
          }
          label={t('box.organizer.renameParticipant.label')}
          error={rename.error ?? undefined}
          testId="game-rename-input"
        />
      ),
      cancel: t('box.cancel'),
      submit: t('box.save'),
      onSubmit: async () => {
        const username = rename.name.trim();
        if (username === rename.card.username) {
          setRename(null);
          return;
        }
        const taken = cards.some(
          (card) =>
            card.id !== rename.card.id &&
            normalizeQuickBoxName(card.username) === normalizeQuickBoxName(username),
        );
        if (taken) {
          setRename((current) =>
            current ? { ...current, error: t('randomizer.problem.duplicate') } : current,
          );
          throw new Error('duplicate');
        }
        const res = await santaClient.updateOtherCard({
          key: info.box.key,
          id: rename.card.id,
          username,
        });
        if (!res.ok) {
          setRename((current) =>
            current ? { ...current, error: t(res.error?.message || 'newBox.saveFailed') } : current,
          );
          throw new Error('rename failed');
        }
        setRename(null);
        await onChanged();
      },
      onCancel: () => setRename(null),
    });
  }, [cards, info.box.key, onChanged, rename, t]);

  useEffect(() => {
    if (!pair) {
      return;
    }
    const subject = pair.kind === 'santa' ? pair.receiver : pair.giver;
    showDialog({
      title: t('box.organizer.giftee.pairTitle'),
      children: (
        <SecretPair
          santa={pair.giver}
          giftee={pair.receiver}
          question={t(
            pair.kind === 'santa'
              ? 'box.organizer.giftee.confirmSanta'
              : 'box.organizer.giftee.confirmGiftee',
            { name: subject.username },
          )}
          revealLabel={t('box.organizer.giftee.confirmYes')}
          santaLabel={t('box.organizer.giftee.santa')}
          gifteeLabel={t('box.organizer.giftee.giftee')}
          testId="game-pair"
        />
      ),
      onCancel: () => setPair(null),
      testId: 'game-pair-dialog',
    });
  }, [pair, t]);

  function rowMenu(card: CardInfo): MenuItem[] {
    if (!info.isAdmin || archived) {
      return [];
    }
    const items: MenuItem[] = [];
    if (!info.isActiveDraw) {
      items.push({
        id: 'exclusions',
        label: t('randomizer.exclusions.configure'),
        onSelect: () => editParticipantExclusions(card),
      });
    }
    const giftee = cards.find((person) => person.id === card.wardId);
    const santa = cards.find((person) => person.wardId === card.id);
    if (info.isActiveDraw && giftee) {
      items.push({
        id: 'giftee',
        label: t('box.organizer.menu.showGiftee'),
        onSelect: () => setPair({ giver: card, receiver: giftee, kind: 'giftee' }),
      });
    }
    if (info.isActiveDraw && santa) {
      items.push({
        id: 'santa',
        label: t('box.organizer.menu.showSanta'),
        onSelect: () => setPair({ giver: santa, receiver: card, kind: 'santa' }),
      });
    }
    items.push({
      id: 'delete',
      label: t('game.people.remove'),
      danger: true,
      onSelect: () => {
        showDialog({
          title: t('game.people.removeTitle'),
          children: t(((card.isYou && info.isAdmin) || info.organizers?.some((person) => person.id === card.id)) ? 'game.people.removeOrganizerBody' : 'game.people.removeBody', { name: card.username }) + (info.isActiveDraw && card.wardId ? ' ' + t('game.people.removeAfterDrawHint') : ''),
          cancel: t('box.cancel'),
          submit: t('game.people.remove'),
          submitVariant: 'danger',
          onSubmit: async () => {
            const res = await santaClient.deleteOtherCard({
              key: info.box.key,
              id: card.id,
            });
            if (!res.ok) {
              showToast(t(res.error?.message || 'game.people.addFailed'));
              throw new Error('delete failed');
            }
            await onChanged();
          },
        });
      },
    });
    return items;
  }

  function confirmReset() {
    showDialog({
      title: t('game.draw.resetTitle'),
      children: t('game.draw.resetBody'),
      cancel: t('box.cancel'),
      submit: t('game.draw.reset'),
      submitVariant: 'danger',
      onSubmit: async () => {
        const res = await santaClient.resetDraw({ key: info.box.key });
        if (!res.ok) {
          if (!handleRenewalError(res.error?.message)) showToast(t(res.error?.message || 'game.draw.failed'));
          throw new Error('reset failed');
        }
        await onChanged();
      },
    });
  }

  function openShared() {
    if (!sharedLink) {
      return;
    }
    sharedMessageRef.current = buildSharedInviteMessage(
      { boxName: info.box.name, isActiveDraw },
      t,
    );
    setSharedOpen(true);
  }

  async function openCopyDialog(card: CardInfo) {
    copyMessageRef.current = buildPersonalInviteMessage(
      { username: card.username, boxName: info.box.name, isActiveDraw },
      t,
    );
    setCopyDialog({
      card,
      link: '',
    });
    const result = await santaClient.createCardAccessLink({
      key: info.box.key,
      id: card.id,
    });
    if (!result.ok || !result.data?.url) {
      setCopyDialog(null);
      closeDialog();
      showToast(t('box.organizer.linkFailed'));
      return;
    }
    const url = sameOriginUrl(result.data.url);
    setCopyDialog((current) =>
      current?.card.id === card.id ? { ...current, link: url } : current,
    );
  }

  function personCells(card: CardInfo) {
    const status = card.isVerified ? 'card_opened' : participantStatus(card, copiedSet);
    const menu = rowMenu(card);
    const canCopy = !card.isYou && !card.isVerified && !archived;
    const canEmail = !card.isYou && !archived;

    return {
      status,
      menu,
      canCopy,
      canEmail,
    };
  }

  const selectionControls =
    canManage && selection.selectedIds.length > 0 ? (
      <div className="game-selection-bar" data-testid="game-selection-bar">
        <Cluster justify="between" gap={3} align="center">
          <Cluster justify="between" align="center" gap={2} className="game-selection-bar__summary">
            <Typography as="p" role="body/sm" aria-live="polite">
              {t('game.people.selectedCount', { count: selection.selectedIds.length })}
            </Typography>
            <Button
              variant="ghost"
              size="small"
              icon={<CloseIcon />}
              aria-label={t('game.people.clearSelection')}
              onClick={selection.clear}
            />
          </Cluster>
          <Cluster gap={2} className="game-selection-bar__actions">
            <Button
              variant="danger"
              size="small"
              onClick={selection.remove}
              testId="game-delete-selected"
            >
              {t('game.people.removeSelected')}
            </Button>
          </Cluster>
        </Cluster>
      </div>
    ) : undefined;

  const addParticipantsForm = !archived && (
    <div className="game-add-participants">
      <NamesInput
        names={adding}
        onNamesChange={(names) => { setAdding(names); setAddingFailed(false); }}
        reservedNames={cards.map(card => card.username)}
        onProblem={setNamesProblem}
        error={namesProblem ? t(`randomizer.problem.${namesProblem}`) : undefined}
        placeholder={t('randomizer.names.placeholder')}
        hint={t('game.people.addParticipantsHint')}
        removeLabel={name => t('randomizer.names.remove', { name })}
        rows={2}
        disabled={addingBusy}
        testId="game-add-names"
      />
      <Button
        variant={adding.length ? "primary" : "secondary"}
        loading={addingBusy}
        disabled={Boolean(namesProblem)}
        onClick={() => void addNames(adding)}
        testId="game-add-submit"
      >
        {t(addingFailed ? 'game.people.addRetry' : 'game.people.addSubmit')}
      </Button>
    </div>
  );

  const participantIds = new Set(cards.map(card => card.id));
  const exclusionsCount = paged ? remote.data?.summary.exclusions ?? 0 : cards.filter(card => card.exclusions?.some(id => id !== card.id && participantIds.has(id))).length;
  const exclusionsSummary = info.isAdmin && <div className="game-draw-exclusions" data-testid="game-draw-exclusions-summary">
    <Stack gap={1}>
      <Typography role="body/strong">{t('game.draw.exclusionsLabel')}</Typography>
      <Typography role="meta/helper">{t(exclusionsCount ? 'game.draw.exclusionsConfigured' : 'game.draw.exclusionsEmpty', { count: exclusionsCount })}</Typography>
    </Stack>
    <Button variant="secondary" size="small" onClick={openExclusions} testId="game-view-exclusions">
      {t('gameExclusions.view')}
    </Button>
  </div>;

  return (
    <Stack gap={5} testId="game-organizer">
      {!archived ? (
        <OrganizerSection gameKey={info.box.key}
          mobile={mobile}
          title={t('game.invitationGuide.title')}
          icon={<LinkIcon />}
          id="invite"
          defaultOpen={paged ? Boolean(remote.data && !remote.data.summary.invited && !copiedSet.size) : !cards.some(card => !card.isYou && participantStatus(card, copiedSet) !== 'not_sent')}
        >
          <Stack gap={5}>
            {canManage && (
              <Stack gap={3} testId="game-add-participants-section">
                <Typography as="h3" role="title/card">{t('game.people.addParticipantsTitle')} <Typography as="span" role="body/sm">({t('game.people.alreadyParticipants', { count: info.participantsNumber })})</Typography></Typography>
                {addParticipantsForm}
              </Stack>
            )}
          <InvitationGuide role={info.isAdmin ? 'organizer' : 'participant'} key={info.box.key} onShared={sharedLink ? openShared : undefined} />
          </Stack>
        </OrganizerSection>
      ) : null}
      <OrganizersPanel info={info} cards={cards} loadCards={loadActionCards} onChanged={onChanged} />
      <BoxActivity key={info.box.key} info={info} cards={cards} />
      <OrganizerSection gameKey={info.box.key}
        mobile={mobile}
        title={<span className="game-draw-heading">{t('game.draw.sectionTitle')}{unassignedCount > 0 && !archived ? <Badge tone="warning" className="game-draw-heading__warning" testId="game-draw-needs-attention">{t('game.draw.needsAttention')}</Badge> : isActiveDraw && <Badge tone="success" className="game-draw-heading__done" testId="game-draw-completed">✓ {t('game.draw.completedLabel')}</Badge>}</span>}
        icon={<GiftIcon />}
        id="draw"
        defaultOpen={!archived && (unassignedCount > 0 || (readyToDraw && !isActiveDraw))}
      >
        <Stack gap={4} className="game-draw-panel" testId="game-draw-section">
          {isActiveDraw ? <Stack gap={5}>
            {unassignedCount > 0 && !archived && <Hint tone="warning" testId="game-unassigned-warning">{t('game.people.unassignedWarning')}</Hint>}
            <div className="game-draw-panel__layout game-draw-complete" data-testid="game-draw-complete">
              <div className="game-draw-complete__message">
                <ThemeArtwork role="gift" className="game-draw-complete__gift" />
                <Stack gap={3} className="game-draw-complete__copy">
                  <Typography as="p" role="body/strong">{t('game.draw.completed.title')}</Typography>
                  <Typography as="p" role="body/base">{t('game.draw.completed.body')}</Typography>
                  <Typography as="p" role="body/base">{t(hasPremiumFeature(info.box, 'boxDraw') ? 'game.draw.completed.premiumHint' : 'game.draw.completed.hint')}</Typography>
                </Stack>
              </div>
              {!archived && <div className="game-draw-panel__action game-draw-complete__actions">
                <Button variant="secondary" fullWidth icon={<RefreshIcon />} onClick={confirmReset} testId="game-reset-draw">{t('game.draw.reset')}</Button>
                <Button variant={unassignedCount > 0 ? "primary" : "secondary"} fullWidth icon={<WrenchIcon />} onClick={editDraw} testId="game-edit-draw" className="game-draw-complete__edit">{t(unassignedCount > 0 ? 'game.draw.fixDraw' : 'game.draw.completed.viewOrEdit')}</Button>
              </div>}
            </div>
          </Stack> : <div className="game-draw-panel__layout">
            <Stack gap={3}>
              <Hint tone={archived ? 'info' : readyToDraw ? 'success' : 'warning'} testId="game-draw-readiness">
                <Stack gap={1}>
                  <Typography role="body/strong" color="inherit">{t(archived ? 'game.draw.archivedTitle' : readyToDraw ? 'game.draw.readyTitle' : 'game.draw.needPeopleTitle')}</Typography>
                  <Typography role="meta/helper" color="inherit">{t(archived ? 'game.draw.archivedHint' : readyToDraw ? 'game.draw.readyShortHint' : 'game.draw.needPeople')}</Typography>
                </Stack>
              </Hint>
              <Stack gap={3} className="game-draw-settings">
                <Typography role="body/strong">{t('game.draw.settingsTitle')}</Typography>
              <div className="game-circle-setting">
              <Switch
                checked={Boolean(info.box.useCircleDraw)}
                disabled={archived || isActiveDraw || savingDrawMode}
                label={t('game.settings.circle')}
                hint={t('game.draw.circleHelp.shortHint')}
                onCheckedChange={(useCircleDraw) => void saveDrawMode(useCircleDraw)}
                testId="game-circle-draw"
              />
                <InlineLink variant="inline" testId="game-circle-help" onClick={questionOpener('draw_circle', info.isAdmin ? 'organizer' : 'participant', () => showDialog({
                  title: t('game.draw.circleHelp.title'), children: <CircleDrawHelp />,
                  submit: t('game.draw.circleHelp.close'), testId: 'circle-draw-help',
                }))}>{t('game.draw.circleHelp.whatIsIt')}</InlineLink>
              </div>
                {exclusionsSummary}
              </Stack>
              <ul className="game-draw-panel__help-links" aria-label={t('game.draw.helpQuestions')}>
                {[
                  { id: 'odd', label: t('game.draw.circleHelp.oddQuestion'), title: t('game.draw.circleHelp.title'), body: <CircleDrawHelp />, dialog: 'circle-draw-help' },
                ].map((question) => <li key={question.id}><QuestionIcon /><InlineLink variant="inline" testId={`game-${question.id}-help`} onClick={questionOpener('draw_odd_participants', info.isAdmin ? 'organizer' : 'participant', () => showDialog({ title: question.title, children: question.body, submit: t('game.draw.circleHelp.close'), testId: question.dialog }))}>{question.label}</InlineLink></li>)}
              </ul>
            </Stack>
            {!archived ? (
              <div className="game-draw-panel__action">
                {!isActiveDraw && readyToDraw && <div className="game-draw-panel__celebration" aria-hidden="true">
                  <ThemeArtwork role="gift" className="game-draw-panel__gift" />
                  <span>{t('game.draw.magicTime')}</span>
                </div>}
              <Button
                variant={isActiveDraw ? 'secondary' : 'primary'}
                icon={isActiveDraw ? <RefreshIcon /> : <GiftIcon />}
                size="large"
                disabled={savingDrawMode || (!isActiveDraw && !readyToDraw)}
                onClick={isActiveDraw ? confirmReset : confirmDraw}
                testId={isActiveDraw ? 'game-reset-draw' : 'game-conduct-draw'}
              >
                {t(isActiveDraw ? 'game.draw.reset' : 'game.draw.submit')}
              </Button>
                <Cluster gap={2} justify="center">
                  <QuestionIcon />
                  <InlineLink variant="inline" testId="game-after-help" onClick={questionOpener('draw_after', info.isAdmin ? 'organizer' : 'participant', () => showDialog({
                    title: t('game.draw.afterHelp.title'), children: <AfterDrawHelp />,
                    submit: t('game.draw.circleHelp.close'), testId: 'after-draw-help',
                  }))}>{t('game.draw.afterHelp.title')}</InlineLink>
                </Cluster>
              </div>
            ) : null}
          </div>}

        </Stack>
      </OrganizerSection>
      <div className="game-participants" aria-busy={paged && remote.loading}>
        <Stack gap={4}>
          {paged && remote.error && <Hint tone="error" testId="game-participants-error">
            {t('errors.UNKNOWN')} <Button variant="secondary" size="small" onClick={() => setPageRetry(value => value + 1)}>{t('premiumBuy.offerRetry')}</Button>
          </Hint>}
          <ParticipantTableFill viewportPage>
            <ParticipantTable
              ref={tableRef}
              label={t('box.organizer.filters.title')}
              testId="game-participants"
              composeDivider={false}
              toolbarContent={() => (
                <div className="game-participants__filters">
                  {canManage && (
                    <Checkbox
                      fit
                      checked={participants.visible.length > 0 && participants.visible.every((card) => selection.selectedIds.includes(card.id))}
                      disabled={!participants.visible.length}
                      onCheckedChange={(checked) => selection.toggleMany(participants.visible.map((card) => card.id), checked)}
                      aria-label={t('box.organizer.filters.selectPage')}
                      testId="game-select-page"
                    />
                  )}
                  <ParticipantSearch
                    query={query}
                    onQueryChange={participants.setSearch}
                    selected={statusFilters}
                    onSelectedChange={(values) => { setStatusFilters(values); participants.setPage(1); }}
                    placeholder={t('box.organizer.searchPlaceholder')}
                    filtersLabel={t('box.organizer.filters.label')}
                    closeLabel={t('box.close')}
                    removeLabel={(label) => t('box.organizer.filters.remove', { label })}
                    options={['unassigned', 'unseen', 'unopened', 'incomplete', 'unsent', 'unreceived'].map((value) => ({ value, label: t(`box.organizer.filters.${value}`) }))}
                  />
                </div>
              )}
              footer={selectionControls}
              compactSearch
              rangeBelowToolbar
              rangeControls={
                <Select
                  className="game-participants__page-size"
                  inline
                  selectedLabel={t('box.organizer.filters.show', { count: pageSizeChoice === 'auto' ? t('box.organizer.filters.auto') : pageSizeChoice })}
                  value={pageSizeChoice}
                  onValueChange={(value) => { setPageSizeChoice(value); participants.setPage(1); }}
                  aria-label={t('box.organizer.filters.pageSize')}
                  options={['auto', '10', '25', '50', '100'].map((value) => ({ value, label: value === 'auto' ? t('box.organizer.filters.auto') : value }))}
                  testId="game-participants-page-size"
                />
              }
              showSearch={participants.showSearch}
              isEmpty={participants.isEmpty}
              empty={
                <Typography as="p" role="body/base">
                  {t(paged && remote.loading ? 'game.people.loadingParticipants' : query.trim() || statusFilters.length > 0 ? 'box.organizer.noResults' : 'game.people.empty')}
                </Typography>
              }
              pagination={{
                page: participants.page,
                pageCount: participants.pageCount,
                onChange: participants.setPage,
                total: paged ? remote.data?.total ?? 0 : participants.filtered.length,
                pageSize: participants.pageSize ?? participants.filtered.length,
                previousLabel: t('box.organizer.pages.previous'),
                nextLabel: t('box.organizer.pages.next'),
                pageLabel: (page) => t('box.organizer.pages.page', { page }),
                testId: 'game-participants-pages',
              }}
            >
              <ParticipantTableRowList
                columns={!mobile ? [
                  ...(['participant', 'outgoing', 'incoming', 'giftee'] as const).map((column) => ({
                    label: t(`box.organizer.table.${column}`),
                    sortDirection: sorts.some((sort) => sort.column === column) ? (sorts.find((sort) => sort.column === column)!.descending ? 'descending' as const : 'ascending' as const) : undefined,
                    sortPriority: sorts.findIndex((sort) => sort.column === column) + 1 || undefined,
                    onSort: () => {
                      setSorts((current) => {
                        const existing = current.find((sort) => sort.column === column);
                        if (!existing) return [...current, { column, descending: false }];
                        return existing.descending ? current.filter((sort) => sort.column !== column) : current.map((sort) => sort.column === column ? { ...sort, descending: true } : sort);
                      });
                      participants.setPage(1);
                    },
                  })),
                  { label: t('game.people.columnActions') },
                ] : undefined}
                placeholderCount={mobile || pageSizeChoice !== 'auto' ? 0 : participants.placeholderCount}
              >
                {participants.visible.map((card, index) => {
                  const cells = personCells(card);
                  const ordinal = (participants.page - 1) * (participants.pageSize ?? 0) + index;
                  return (
                    <ParticipantRow
                      key={card.id}
                      card={card}
                      tableRow={!mobile}
                      showOrdinal={!mobile}
                      selection={
                        canManage ? (
                          <Checkbox
                            fit
                            checked={selection.selectedIds.includes(card.id)}
                            onCheckedChange={(checked) => selection.toggleMany([card.id], checked)}
                            aria-label={t('game.people.selectPerson', { name: card.username })}
                            testId={`game-select-${card.id}`}
                          />
                        ) : undefined
                      }
                      index={ordinal}
                      ordinal={ordinal + 1}
                      status={
                        mobile || cells.status === 'ward_received'
                          ? undefined
                          : cells.status === 'not_sent'
                            ? undefined
                            : t(`box.organizer.status.${cells.status}`)
                      }
                      statusTone={participantStatusTone(cells.status)}
                      statusDisplay="text"
                      statusColumns={!mobile ? <ParticipantStatusColumns card={card} people={cards} tableCells /> : undefined}
                      description={
                        <>

                          {mobile && <ParticipantStatusColumns card={card} people={cards} />}
                        </>
                      }
                      youLabel={card.isYou ? t('box.organizer.thatsYou') : undefined}
                      locked={archived}
                      previewLabel={t('box.organizer.preview.title', { name: card.username })}
                      previewMenuLabel={t('box.organizer.menu.viewCard')}
                      onPreview={
                        info.isAdmin
                          ? () =>
                              openParticipantPreview({ onChanged, isActiveDraw: Boolean(info.isActiveDraw),
                                card,
                                people: cards,
                                box: info.box,
                                index: ordinal,
                                copiedIds: copiedSet,
                              })
                          : undefined
                      }
                      onRename={card.isYou || card.canOrganizerEditIdentity ? () => setRename({ card, name: card.username || '', error: null }) : undefined}
                      onCopy={cells.canCopy ? () => void openCopyDialog(card) : undefined}
                      copyLabel={t('box.organizer.menu.copy')}
                      onEmail={
                        cells.canEmail
                          ? () =>
                              setEmailDialog({
                                card,
                                email: '',
                                consent: false,
                                emailError: null,
                                error: null,
                              })
                          : undefined
                      }
                      emailLabel={t('box.organizer.menu.email')}
                      onMessage={
                        !archived && !card.isYou
                          ? () => navigate(boxMessengerPath(info.box.key, `user-${card.id}`))
                          : undefined
                      }
                      messageLabel={t('game.people.message', {
                        name: card.username || t('game.people.hidden'),
                      })}
                      menu={cells.menu}
                      menuLabel={t('box.organizer.menu.more')}
                      testId={`game-person-${card.id}`}
                    />
                  );
                })}
              </ParticipantTableRowList>
            </ParticipantTable>
          </ParticipantTableFill>
        </Stack>
      </div>
    </Stack>
  );
}
